import { CALCULATE_CART } from "@/actions/CALCULATE_CART.ts";
import { WarningToast } from "@/components/Notifications/WarningToast.tsx";
import { createPersonsInputHelper } from "@/components/ReservationSidebar/helpers/createPersonsInputHelper.ts";
import { areCartAccItemsDeeplyEqual } from "@/helpers/areCartAccItemsDeeplyEqual.ts";
import { CalculateCartInput, CalculateCartPayload, CartAccommodationInput } from "@/helpers/types/types.ts";
import { findRatePlanIdPerOccupancy } from "@/pages/ReservationOverview/findRatePlanIdPerOccupancy.ts";
import { getExtrasPayload } from "@/pages/ReservationOverview/getExtrasPayload.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { useMutation } from "@apollo/client";
import format from "date-fns/format";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

interface CCPayload {
  calculateCart: CalculateCartPayload;
}
interface CCInput {
  input: CalculateCartInput;
  from: string;
  until: string;
}

export const useCalculateCart = (cancelCartCalculate?: boolean) => {
  const { t } = useTranslation();
  const {
    cartAccommodationItems,
    setCalculatedCartData,
    setCalculateCartLoading,
    defaultDateRange,
    calculatedCartData,
  } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
      setCalculatedCartData: state.setCalculatedCartData,
      setCalculateCartLoading: state.setCalculateCartLoading,
      defaultDateRange: state.defaultDateRange,
      calculatedCartData: state.calculatedCartData,
    })),
  );
  const prevCartAccommodationItems = useRef<CartAccommodationItem[]>([]);

  const [calculateCart, { loading: calculateCartLoading, data: calculateCartData }] = useMutation<
    CCPayload,
    CCInput
  >(CALCULATE_CART, {
    errorPolicy: "all",
    onCompleted: (response) => {
      const cart = response?.calculateCart?.cart;
      setCalculatedCartData(cart);
    },
    onError: (e) => {
      const errors: { message: string }[] =
        e.graphQLErrors?.flatMap((GQLError) => {
          if (GQLError.extensions?.validation) {
            return Object.values(GQLError.extensions.validation)?.[0];
          }
        }) ?? [];
      const errorMessages = errors?.map((error) => error.message) ?? [];
      console.error(e?.message);
      console.error(errorMessages.join(", "));
      setCalculatedCartData({ ...calculatedCartData!, error: errorMessages.join(", ") || e?.message });
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    setCalculateCartLoading(calculateCartLoading);
  }, [calculateCartLoading, setCalculateCartLoading]);

  const handleCalculateCart = useCallback(() => {
    if (!defaultDateRange[0] || !defaultDateRange[1]) return;
    if (cancelCartCalculate) {
      if (!cartAccommodationItems?.length) {
        setCalculatedCartData(undefined);
      }
      return;
    }
    if (!areCartAccItemsDeeplyEqual(prevCartAccommodationItems.current, cartAccommodationItems)) {
      prevCartAccommodationItems.current = cartAccommodationItems;

      if (!cartAccommodationItems?.length) {
        setCalculatedCartData(undefined);
        return;
      }

      const accommodations: CartAccommodationInput[] = [];

      try {
        cartAccommodationItems.forEach((CAI) => {
          if (!CAI.dateRange?.[0] || !CAI.dateRange?.[1]) return;

          const persons = createPersonsInputHelper(CAI.adults, CAI.children);
          const ratePlanId = findRatePlanIdPerOccupancy(CAI.ratePlan, persons.length);
          const extras = getExtrasPayload(CAI.optionalExtras);

          accommodations.push({
            identifier: CAI.id,
            checkIn: format(new Date(CAI.dateRange[0]), "yyyy-MM-dd"),
            checkOut: format(new Date(CAI.dateRange[1]), "yyyy-MM-dd"),
            extras,
            persons,
            ratePlanId,
          });
        });
      } catch (e) {
        console.error(e);
        toast.custom(<WarningToast message={t("form.submitErr") as string} />);
        return;
      }

      const from = format(new Date(defaultDateRange[0]), "yyyy-MM-dd");
      const until = format(new Date(defaultDateRange[1]), "yyyy-MM-dd");

      return calculateCart({ variables: { input: { accommodations }, from, until } }).catch((error) => {
        console.error(error);
      });
    }
  }, [
    defaultDateRange,
    cancelCartCalculate,
    cartAccommodationItems,
    setCalculatedCartData,
    calculateCart,
    t,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCalculateCart = useCallback(
    debounce(() => {
      void handleCalculateCart();
    }, 100),
    [handleCalculateCart],
  );

  return { handleCalculateCart, debouncedCalculateCart, calculateCartLoading, calculateCartData };
};
